var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import * as React from 'react';
import clsx from 'clsx';
import { useConsoleLog } from '@hawkins/helpers';
import { DEFAULT_GET_OPTION_LABEL, DEFAULT_GET_OPTION_VALUE, DROPDOWN_LIST_GROUP_HEADER_CLASS, } from '../../dropdown.constants';
import { checkDuplicatedOptionValues, getVirtualizedOptionHeight } from '../../util';
import { DropdownListGroupHeaderDefault } from './dropdownCommon';
import { DropdownListItem as DropdownListItemDefault } from './dropdownListItem';
import { VirtualizedList } from './virtualizedList';
var displayName = 'DropdownListBody';
/**
 * Default list body for the dropdown
 * @param props
 * @returns
 */
export function DropdownListBody(props) {
    var _a;
    var _b, _c;
    var classes = props.classes, computedRecentSelected = props.computedRecentSelected, _d = props.getOptionLabel, getOptionLabel = _d === void 0 ? DEFAULT_GET_OPTION_LABEL : _d, _e = props.getOptionValue, getOptionValue = _e === void 0 ? DEFAULT_GET_OPTION_VALUE : _e, options = props.options, virtualize = props.virtualize, virtualizedListRef = props.virtualizedListRef, virtuosoRef = props.virtuosoRef, handleSelected = props.handleSelected, multi = props.multi, query = props.query, selectedIndex = props.selectedIndex, selectedOptionsIds = props.selectedOptionsIds, getOptionDisabled = props.getOptionDisabled, _f = props.DropdownListGroupHeader, DropdownListGroupHeader = _f === void 0 ? DropdownListGroupHeaderDefault : _f, _g = props.DropdownListItem, DropdownListItem = _g === void 0 ? DropdownListItemDefault : _g;
    var dropdownClassName = clsx(classes === null || classes === void 0 ? void 0 : classes.dropdown, classes === null || classes === void 0 ? void 0 : classes.dropdownContainer, (_a = {},
        _a[(_b = classes === null || classes === void 0 ? void 0 : classes.dropdownListBodyVirtualized) !== null && _b !== void 0 ? _b : ''] = virtualize,
        _a[(_c = classes === null || classes === void 0 ? void 0 : classes.dropdownListBody) !== null && _c !== void 0 ? _c : ''] = !virtualize,
        _a));
    useConsoleLog(checkDuplicatedOptionValues(options, getOptionValue), 'We detected duplicated keys after rendering the Dropdown options, this can create unexpected behavior.', {
        displayName: displayName,
        docsPath: 'components-dropdown#DropdownList',
        level: 'warn',
        printInProd: true,
    });
    // memos
    /**
     * Due to the nature of how we're merging the group headers
     * into the options list, and in order to prevent further prop drilling,
     * we need to offset the selectedIndex based on the number of string "group headers" that
     * have come before in the computed options.
     * Since we know the object references between the inbound "options" prop and the "computedOptions"
     * prop are the same, we can loop until we find the object match and return its index
     */
    var computedSelectedIndex = React.useMemo(function () {
        if (typeof selectedIndex !== 'number')
            return null;
        var cursor = 0;
        for (var i = 0; i < options.length; i++) {
            var opt = options[i];
            if (typeof opt === 'string')
                continue;
            if (cursor === selectedIndex)
                return i;
            cursor += 1;
        }
        return null;
    }, [options, selectedIndex]);
    var renderOption = function (option, index) {
        var _a;
        return (_createElement(DropdownListItem, __assign({}, props, { key: "".concat(getOptionLabel(option), " - ").concat(getOptionValue(option)), index: index, item: option, multi: multi, onSelected: handleSelected, option: option, query: query, selectedIndex: computedSelectedIndex, selectedOptionsIds: selectedOptionsIds, style: {
                height: props.virtualize
                    ? getVirtualizedOptionHeight(option, __assign(__assign({}, props.virtualizeProps), { compact: props.compact }))
                    : undefined,
            }, 
            /** kept to maintain compatibility with previous dropdown version */
            optionProps: {
                'aria-disabled': getOptionDisabled(option),
                'aria-selected': computedSelectedIndex === index,
                'data-option-index': (_a = option.index) !== null && _a !== void 0 ? _a : index,
                key: "result-".concat(getOptionValue(option)),
                id: "result-".concat(getOptionValue(option)),
                role: 'option',
                tabIndex: -1,
            } })));
    };
    var renderOptionGroupHeader = function (option) { return (_createElement(DropdownListGroupHeader, __assign({}, props, { className: DROPDOWN_LIST_GROUP_HEADER_CLASS, key: "group-header-".concat(option), optionKey: option, style: {
            height: props.virtualize
                ? getVirtualizedOptionHeight(option, __assign(__assign({}, props.virtualizeProps), { compact: props.compact }))
                : undefined,
        } }), option)); };
    return (_jsxs(VirtualizedList, __assign({ className: dropdownClassName, classes: { listWrapper: classes === null || classes === void 0 ? void 0 : classes.dropdownVirtualizedListWrapper }, enabled: Boolean(virtualize), ref: virtualizedListRef, virtuosoRef: virtuosoRef }, { children: [computedRecentSelected.options.map(function (option, index) {
                return typeof option === 'string' ? renderOptionGroupHeader(option) : renderOption(option, index);
            }), options.map(function (option, index) {
                return typeof option === 'string' ? renderOptionGroupHeader(option) : renderOption(option, index);
            })] })));
}
DropdownListBody.displayName = displayName;
